import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { MaterialReactTable } from 'material-react-table';
import Select from 'react-select';
import {
  Box, Button, createTheme, ThemeProvider, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid,
} from '@mui/material';
import { AccountCircle, Send, Attachment as AttachmentIcon, FileDownload, Print as PrintIcon } from '@mui/icons-material';
import AllInputs from '../../../../inputs/AllInputs';
import { getDateTime, readableStamp } from '../../../../utils/dateFunctions';
import TextFieldGroup from '../../../../inputs/TextFieldGroup';
import {
  appraisalRequestNotification,
  appraisalNotification,
} from '../../../../actions/apiNotifications';
import {
  lookupUserById,
  lookupDepartmentById,
  lookupDepartmentByName,
  lookupVehicleEstimateById,
  vsearch,
  lookupRecipients,
} from '../../../../actions/lookups';
import { deleteAttachmentWithRefresh } from '../../../../actions/apiAttachments';
import {
  addEstimate, sendEstimate, deleteEstimate,
} from '../../../../actions/apiEstimates';
import VehiclePartsSection from '../../../../components/Parts/VehiclePartsSection';
import AppraisalPartsSection from '../../../../components/Parts/AppraisalPartsSection';
import AppraisalAttachments from '../../../../components/Parts/AppraisalAttachments';
import { tabWorkItemPartsEnabled, presetPartsToItems } from '../../../../components/Parts/partsUtils';
import { addWorkItemPartsBulk } from '../../../../actions/apiWorkItemParts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mkConfig, generateCsv, download } from 'export-to-csv';

const now = new Date();
const month = (now.getMonth() + 1).toString().padStart(2, '0');
const day = now.getDate().toString().padStart(2, '0');
const year = now.getFullYear();
const filename = `work_items_${month}_${day}_${year}`;

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
  filename,
});

const NewEstimates = ({ tab }) => {
  const dispatch = useDispatch();
  const [confirmingTask, setConfirmingTask] = useState(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const tableRef = useRef();
  const activeVehicle = useSelector((state) => state.settings_refresh.active_vehicle);
  const all_tabs = useSelector((state) => state.settings_site?.all_tabs || []);
  const workItemPartsEnabled = tabWorkItemPartsEnabled(all_tabs, tab);
  const appraisals = useSelector((state) => state.settings_refresh.appraisals);
  const workItemPresets = useSelector((state) => state.settings_refresh.work_item_presets);
  const catalog_parts = useSelector((state) => state.settings_refresh?.catalog_parts || []);
  const dealerSettings = useSelector((state) => state.settings_site.dealer_settings);
  const installation = useSelector((state) => state.settings_dealerships.installation);
  const currentDealership = useSelector((state) => state.settings_dealerships.current_dealership);
  const currentUser = useSelector((state) => state.current_user);
  const [newdata, setNewdata] = useState([]);
  const [estimates_total, setEstimatesTotal] = useState(0);
  const currentUserProfile = useSelector((state) => state.settings_users.current_user_profile);

  // Initialize and debug isDisabled based on user permissions
  const [isDisabled, setIsDisabled] = useState(true);
  const [canUndo, setCanUndo] = useState(true);
  const [editEstimates, setEditEstimates] = useState(true);
  const [cantApprove, setCantApprove] = useState(true);
  const [canDelete, setCanDelete] = useState(true);
  useEffect(() => {
    if (currentUser) {
      const hasApproveEstimates = currentUser.approve_estimates || currentUserProfile.work_item_approve;
      const hasEditEstimates = currentUserProfile.work_item_edit;
      console.log(currentUser);
      console.log(currentUserProfile);
      const hasAdminPosition = ['admin', 'super_admin'].includes(currentUser.position);
      const hasPermission = hasApproveEstimates || hasAdminPosition || hasEditEstimates;
      const hasCanApprove = hasApproveEstimates || hasAdminPosition;
      const hasDeleteEstimates = currentUserProfile.work_item_delete || hasAdminPosition;
      setIsDisabled(!hasPermission);
      setCanUndo(hasCanApprove);
      setEditEstimates(editEstimates);
      setCantApprove(!hasCanApprove);
      setCanDelete(!hasDeleteEstimates);
    } else {
      setIsDisabled(true);
    }
  }, [currentUser, currentUserProfile]);

  const globalTheme = useTheme();

  const tableTheme = useMemo(
    () => createTheme({
      palette: {
        mode: 'light',
      },
    }),
    [globalTheme],
  );

  // ====================== DEALERSHIP FILTERING ======================
  const visibleWorkItemPresets = useMemo(() => {
    if (!currentDealership) return workItemPresets;

    return workItemPresets.filter((preset) => {
      const dealershipField = preset.dealership || '';
      if (!dealershipField.trim()) return true; // blank = available to all dealerships
      const allowedDealerships = dealershipField.split(',').map(d => d.trim());
      return allowedDealerships.includes(currentDealership);
    });
  }, [workItemPresets, currentDealership]);

  // Calculate totals for parts, labor, and approved
  const partsTotal = useMemo(
    () =>
      newdata.reduce(
        (sum, row) => sum + (Number(row.parts_price[0]) || 0),
        0
      ),
    [newdata]
  );

  const laborTotal = useMemo(
    () =>
      newdata.reduce(
        (sum, row) =>
          sum + (Number(row.labor_units[0]) * Number(row.labor_price[0]) || 0),
        0
      ),
    [newdata]
  );

  const approvedTotal = useMemo(
    () =>
      newdata.reduce(
        (sum, row) => {
          const calculated = Number(row.parts_price[0]) + (Number(row.labor_units[0]) * Number(row.labor_price[0]));
          const stored = Number(row.estimate_total[0]) || 0;
          const counter = Number(row.counter_estimate[0]) || 0;
          if (row.ro_status[0] === 'declined' && counter > 0) {
            return sum + counter;
          }
          return ['approved', 'completed'].includes(row.ro_status[0])
            ? sum + (stored > 0 ? stored : calculated)
            : sum;
        },
        0
      ),
    [newdata]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'category',
        header: 'Classification',
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: false,
        size: 90,
        accessorFn: (row) => row.category[0],
        Cell: ({ cell }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {activeVehicle &&
              activeVehicle.attachments.some(
                (attachment) => attachment.appraisal_id === cell.row.original.estimate_id[0]
              ) && (
                <AttachmentIcon
                  sx={{ fontSize: '1rem', transform: 'rotate(90deg)', color: 'green' }}
                />
              )}
            {cell.getValue() || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'label_item_1',
        header: 'Work Item',
        size: 160,
        muiTableHeadCellProps: {
          style: {
            paddingTop: '0.75rem',
          },
        },
        enableEditing: true,
        accessorFn: (row) => row.label_item_1[0],
        muiEditTextFieldProps: ({ cell, row }) => ({
          disabled: row.original.lock_estimate[0] || isDisabled,
          onBlur: (event) => {
            const data = {
              vehicle_id: activeVehicle.id,
              label_item_1: event.target.value,
            };
            dispatch(sendEstimate(row.original.estimate_id[0], data, 'addEstimateError'));
          },
        }),
        Cell: ({ cell, row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.estimate_type[0] === 'blank_parts_and_labor' ? (
              cell.getValue() || 'N/A'
            ) : (
              <Select
                titleHelper={'Type'}
                title={'Choose'}
                name={'WorkItem'}
                value={selectWorkItem().filter(
                  ({ value }) => value === row.original.label_item_1[0]
                )}
                options={selectWorkItem()}
                clearable={false}
                searchable
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                className="text-capitalize"
                isDisabled={row.original.lock_estimate[0] || isDisabled}
                vehicle={activeVehicle.vehicle_id}
                onChange={onChangeWorkItem(row.original.estimate_id[0])}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
              />
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 200,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: true,
        accessorFn: (row) => row.description[0],
        muiEditTextFieldProps: ({ cell, row }) => ({
          disabled: ['approved', 'completed'].includes(row.original.ro_status[0]) || isDisabled,
          multiline: true,
          onBlur: (event) => {
            const data = {
              vehicle_id: activeVehicle.id,
              description: event.target.value,
            };
            dispatch(sendEstimate(row.original.estimate_id[0], data, 'addEstimateError'));
          },
        }),
        Cell: ({ cell }) => (
          <Box sx={{ 
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            minWidth: '200px',
          }}>
            {cell.getValue() || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'parts_price',
        header: 'Parts',
        size: 25,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: true,
        accessorFn: (row) => row.parts_price[0],
        muiEditTextFieldProps: ({ cell, row }) => ({
          disabled:
            row.original.ro_status[0] === 'pending'
              ? !row.original.lock_item_1 || isDisabled
              : row.original.lock_estimate[0] || isDisabled,
          onBlur: (event) => {
            const parts_price = Number(event.target.value);
            const labor_total = Number(row.original.labor_units[0]) * Number(row.original.labor_price[0]);
            const estimate_total = parts_price + labor_total;
            const data = {
              vehicle_id: activeVehicle.id,
              parts_price: parts_price,
              estimate_total: estimate_total,
              estimate: estimate_total,
            };
            dispatch(sendEstimate(row.original.estimate_id[0], data, 'addEstimateError'));
          },
        }),
      },
      {
        accessorKey: 'labor_units',
        header: 'Labor Units',
        size: 25,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: true,
        accessorFn: (row) => row.labor_units[0],
        muiEditTextFieldProps: ({ cell, row }) => ({
          disabled:
            row.original.ro_status[0] === 'pending'
              ? !row.original.lock_item_2 || isDisabled
              : row.original.lock_estimate[0] || isDisabled,
          onBlur: (event) => {
            const labor_units = Number(event.target.value);
            const labor_price = Number(row.original.labor_price[0]) || 
              Number(_.find(dealerSettings, (o) => o.key === 'default_labor_rate' && o.dealership === installation.subdomain)?.value) || 100;
            const labor_total = labor_units * labor_price;
            const estimate_total = Number(row.original.parts_price[0]) + labor_total;
            const data = {
              vehicle_id: activeVehicle.id,
              labor_units: labor_units,
              estimate_total: estimate_total,
              estimate: estimate_total,
            };
            dispatch(sendEstimate(row.original.estimate_id[0], data, 'addEstimateError'));
          },
        }),
      },
      {
        accessorKey: 'labor_price',
        header: 'Labor Rate',
        size: 25,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: true,
        accessorFn: (row) => row.labor_price[0],
        muiEditTextFieldProps: ({ cell, row }) => ({
          disabled:
            row.original.ro_status[0] === 'pending'
              ? !row.original.lock_item_3 || isDisabled
              : row.original.lock_estimate[0] || isDisabled,
          onBlur: (event) => {
            const labor_price = Number(event.target.value);
            const labor_total = Number(row.original.labor_units[0]) * labor_price;
            const estimate_total = Number(row.original.parts_price[0]) + labor_total;
            const data = {
              vehicle_id: activeVehicle.id,
              labor_price: labor_price,
              estimate_total: estimate_total,
              estimate: estimate_total,
            };
            dispatch(sendEstimate(row.original.estimate_id[0], data, 'addEstimateError'));
          },
        }),
      },
      {
        accessorKey: 'labor_total',
        header: 'Labor Total',
        size: 25,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: false,
        accessorFn: (row) =>
          (Number(row.labor_units[0]) * Number(row.labor_price[0])).toFixed(2),
        Cell: ({ cell }) => cell.getValue() || '0.00',
      },
      {
        accessorKey: 'estimate_total',
        header: 'Total',
        size: 25,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5rem',
          },
        },
        enableEditing: true,
        accessorFn: (row) => row.estimate_total[0],
        muiEditTextFieldProps: ({ cell, row }) => ({
          disabled:
            row.original.ro_status[0] === 'pending'
              ? !row.original.lock_item_4 || isDisabled
              : row.original.lock_estimate[0] || isDisabled,
          onBlur: (event) => {
            const estimate_total = Number(event.target.value);
            const data = {
              vehicle_id: activeVehicle.id,
              estimate_total: estimate_total,
              estimate: estimate_total,
            };
            dispatch(sendEstimate(row.original.estimate_id[0], data, 'addEstimateError'));
          },
        }),
        Cell: ({ row }) => {
          const calculated = Number(row.original.parts_price[0]) + (Number(row.original.labor_units[0]) * Number(row.original.labor_price[0]));
          const stored = Number(row.original.estimate_total[0]) || 0;
          return (stored > 0 ? stored : calculated).toFixed(2);
        },
      },
      {
        accessorKey: 'ro_status',
        enableEditing: false,
        header: 'Status',
        size: 100,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5cm',
          },
        },
        accessorFn: (row) => row.ro_status[0],
        Cell: ({ cell, row }) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
            {(() => {
              switch (cell.getValue()) {
                case 'pending':
                  return (
                    <div className={'btn-group'} role="group" style={{ width: '200px' }}>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={onApprove(row.original)}
                        disabled={cantApprove}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={onDeny(row.original)}
                        disabled={cantApprove}
                      >
                        Deny
                      </button>
                    </div>
                  );
                case 'approved':
                case 'completed':
                  return (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                          style={{
                            width: '220px',
                            overflow: 'auto',
                            textWrap: 'auto',
                            textOverflow: 'ellipsis',
                            overflowX: 'hidden',
                          }}
                          className="btn btn-sm btn-success text-capitalize"
                          title={
                            row.original.approved_by_uid[0]
                              ? dispatch(lookupUserById(row.original.approved_by_uid[0])).user_name
                              : null
                          }
                        >
                          Approved{' '}
                          {row.original.approved_by_uid[0]
                            ? dispatch(lookupUserById(row.original.approved_by_uid[0])).user_name
                            : ''}{' '}
                          {row.original.approved_date[0]
                            ? readableStamp(row.original.approved_date[0])
                            : ''}
                        </button>
                        {canUndo && (
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={onUndo(row.original)}
                          >
                            Undo
                          </button>
                        )}
                      </Box>
                      {cell.getValue() === 'approved' ? (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={onComplete(row.original)}
                          disabled={cantApprove}
                        >
                          Complete
                        </button>
                      ) : (
                        <button
                          style={{
                            width: '220px',
                            overflow: 'auto',
                            textWrap: 'auto',
                            textOverflow: 'ellipsis',
                            overflowX: 'hidden',
                          }}
                          className="btn btn-sm btn-success text-capitalize"
                          disabled
                          title={
                            row.original.completed_by_uid[0]
                              ? dispatch(lookupUserById(row.original.completed_by_uid[0])).user_name
                              : null
                          }
                        >
                          Completed{' '}
                          {row.original.completed_by_uid[0]
                            ? dispatch(lookupUserById(row.original.completed_by_uid[0])).user_name
                            : ''}{' '}
                          {row.original.completed_date[0]
                            ? readableStamp(row.original.completed_date[0])
                            : ''}
                        </button>
                      )}
                    </>
                  );
                case 'declined':
                  return (
                    <div style={{ height: '85px' }}>
                      <div>
                        <button
                          style={{
                            width: '220px',
                            overflow: 'auto',
                            textWrap: 'auto',
                            textOverflow: 'ellipsis',
                            overflowX: 'hidden',
                          }}
                          className="btn btn-sm btn-danger"
                          title={
                            row.original.declined_by_uid[0]
                              ? dispatch(lookupUserById(row.original.declined_by_uid[0])).user_name
                              : null
                          }
                        >
                          Denied{' '}
                          {row.original.declined_by_uid[0]
                            ? dispatch(lookupUserById(row.original.declined_by_uid[0])).user_name
                            : ''}{' '}
                          {row.original.declined_date[0]
                            ? readableStamp(row.original.declined_date[0])
                            : ''}
                        </button>
                        {canUndo ? (
                          <button
                            className="btn btn-sm btn-warning space-left-1"
                            onClick={onUndo(row.original)}
                          >
                            Undo
                          </button>
                        ) : (
                          ''
                        )}
                      </div>
                      <div>
                        <span className="d-inline">New Approved Total:</span>
                        <AllInputs
                          value={row.original.counter_estimate[0] || ''}
                          type={'dollar'}
                          name={'counter_estimate'}
                          parentid={activeVehicle.id}
                          estimate_id={row.original.estimate_id[0]}
                          updatetype="estimate"
                          user_name={''}
                          updated_at={''}
                          setdisabled={cantApprove}
                          vehicle={activeVehicle}
                        />
                      </div>
                    </div>
                  );
                default:
                  return row.original.recipients[0].length > 0 ? (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={onSend(row.original)}
                    >
                      Get Approved
                    </button>
                  ) : (
                    '(Select Recipient)'
                  );
              }
            })()}
          </Box>
        ),
      },
      {
        accessorKey: 'lock_estimate',
        header: 'Delete',
        size: 25,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 0.5cm',
          },
        },
        enableEditing: false,
        accessorFn: (row) => row.lock_estimate[0],
        Cell: ({ cell, row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingLeft: '8px' }}>
            {row.original.ro_status[0] === 'getapproved' ||
            row.original.ro_status[0] === 'pending' ? (
              <button
                className="btn btn-sm btn-danger"
                data-id={row.original.estimate_id[0]}
                disabled={canDelete}
                onClick={() =>
                  dispatch(
                    deleteEstimate(
                      row.original.estimate_id[0],
                      activeVehicle.id,
                      'addDeleteEstimateError'
                    )
                  )
                }
              >
                X
              </button>
            ) : (
              ''
            )}
          </Box>
        ),
      },
    ],
    [activeVehicle, isDisabled, canUndo, cantApprove, canDelete, dispatch]
  );

  const presetColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Work Item',
        size: 150,
        Cell: ({ row, renderedCellValue }) => {
          const isUsed = appraisals.some(
            (appraisal) => appraisal.label_item_1 === renderedCellValue
          );
          return (
            <Box
              sx={{
                padding: '8px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: selectedPresets[row.index]
                  ? '#e3f2fd'
                  : isUsed
                  ? '#e0e0e0'
                  : 'white',
                flex: '1 1 calc(25% - 8px)',
                minWidth: '0',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
              onClick={() => {
                setSelectedPresets((prev) => {
                  const newSelection = { ...prev };
                  newSelection[row.index] = !newSelection[row.index];
                  return newSelection;
                });
              }}
            >
              {renderedCellValue}
            </Box>
          );
        },
      },
    ],
    [selectedPresets, appraisals]
  );

  // ====================== PRESET DATA - NOW FILTERED BY DEALERSHIP ======================
  const presetData = useMemo(
    () => {
      const filteredPresets = _.sortBy(visibleWorkItemPresets, 'name')
        .filter((preset) =>
          selectedCategory === 'all' ? true : preset.category === selectedCategory
        )
        .map((preset) => ({
          id: preset.id,
          name: preset.name,
          category: preset.category,
          parts_price: preset.parts_price,
          labor_units: preset.labor_units,
          labor_price: preset.labor_price,
          estimate_total: preset.estimate_total,
          estimate_type: preset.estimate_type,
          parts_price_editable: preset.parts_price_editable,
          labor_units_editable: preset.labor_units_editable,
          labor_price_editable: preset.labor_price_editable,
          estimate_total_editable: preset.estimate_total_editable,
          description: preset.description,
        }));
      return filteredPresets;
    },
    [visibleWorkItemPresets, selectedCategory]
  );

  const newvehicle = {
    vehicle_id: activeVehicle.id,
    ro_status: 'pending',
    lock_estimate: 'false',
  };

  const onDelete = (e) => {
    dispatch(deleteEstimate(e.target.dataset.id, activeVehicle.id, 'addDeleteEstimateError'));
  };

  const onDeleteAttachment = (vehicle_id, id) => {
    dispatch(deleteAttachmentWithRefresh(vehicle_id, id));
  };

  const onClickSingle = () => {
    const default_labor_rate = _.find(
      dealerSettings,
      (o) => o.key === 'default_labor_rate' && o.dealership === installation.subdomain
    );
    const default_labor_rate_value = default_labor_rate ? default_labor_rate.value : 100;
    const data = {
      ...newvehicle,
      labor_price: default_labor_rate_value,
    };
    dispatch(addEstimate(data, 'addDeleteEstimateError'));
  };

  const onClickMultiple = () => {
    if (Object.keys(selectedPresets).length === 0) {
      setFlyoutOpen(false);
    } else {
      const default_labor_rate = _.find(
        dealerSettings,
        (o) => o.key === 'default_labor_rate' && o.dealership === installation.subdomain
      );
      const default_labor_rate_value = default_labor_rate ? default_labor_rate.value : 100;
      const selectedRows = Object.keys(selectedPresets)
        .filter((index) => selectedPresets[index])
        .map((index) => presetData[parseInt(index)]);
      const uid = parseInt(currentUser?.user, 10) || null;
      const createOne = (preset) => {
        const lock = preset.name !== 'Blank Row';
        const labor_price = Number(preset.labor_price) > 0 ? Number(preset.labor_price) : default_labor_rate_value;
        const labor_total = Number(preset.labor_units) * labor_price;
        const estimate_total = Number(preset.parts_price) + labor_total;
        const data = {
          estimate_total: estimate_total,
          labor_price: labor_price,
          labor_units: preset.labor_units,
          estimate_type: preset.estimate_type,
          category: preset.category,
          estimate: estimate_total,
          vehicle_id: activeVehicle.id,
          description: preset.description,
          label_item_1: preset.name,
          lock_estimate: lock,
          lock_item_1: preset.parts_price_editable,
          lock_item_2: preset.labor_units_editable,
          lock_item_3: preset.labor_price_editable,
          lock_item_4: preset.estimate_total_editable,
          parts_price: preset.parts_price,
          ro_status: 'pending',
        };
        return dispatch(addEstimate(data, 'addEstimateError')).then(newId => ({ preset, newId }));
      };

      Promise.all(selectedRows.map(createOne)).then((created) => {
        const items = [];
        created.forEach(({ preset, newId }) => {
          if (!newId) return;
          const fullPreset = workItemPresets.find(p => String(p.id) === String(preset.id));
          const basePayload = {
            vehicle_id: activeVehicle.id, work_item_source: 'appraisals', appraisal_id: newId, dealership: installation.subdomain,
          };
          items.push(...presetPartsToItems(fullPreset, catalog_parts, basePayload, uid));
        });
        if (items.length) dispatch(addWorkItemPartsBulk(items, activeVehicle.id));
      });
      setSelectedPresets({});
      setFlyoutOpen(false);
    }
  };

  const selectRecipients = () => {
    const users = dispatch(lookupRecipients());
    const options = users.map((option) => ({
      id: option.id,
      label: option.user_name,
      value: option.id,
    }));
    return options || [];
  };

  const onApprove = (estimate) => () => {
    const subdomain = installation.database;
    const message = 'Estimate Approved';
    const data = {
      requested_by_uid: estimate.requested_by_uid[0],
      ro_status: 'approved',
      approved_by_uid: currentUser.user,
      approved_date: getDateTime(),
      vehicle_id: activeVehicle.id,
      lock_estimate: 'true',
    };
    dispatch(sendEstimate(estimate.estimate_id[0], data, 'addEstimateError'));
  };

  const onDeny = (estimate) => () => {
    const subdomain = installation.database;
    const message = 'Estimate Declined';
    const data = {
      vehicle_id: activeVehicle.id,
      ro_status: 'declined',
      declined_by_uid: currentUser.user,
      declined_date: getDateTime(),
    };
    dispatch(sendEstimate(estimate.estimate_id[0], data, 'addEstimateError'));
  };

  const onUndo = (estimate) => () => {
    const data = {
      vehicle_id: activeVehicle.id,
      declined_by_uid: 0,
      declined_date: '',
      approved_by_uid: estimate.ro_status[0] === 'approved' || estimate.ro_status[0] === 'completed' ? estimate.approved_by_uid[0] : 0,
      approved_date: estimate.ro_status[0] === 'approved' || estimate.ro_status[0] === 'completed' ? estimate.approved_date[0] : '',
      completed_by_uid: 0,
      completed_date: '',
      lock_estimate: estimate.ro_status[0] === 'approved' || estimate.ro_status[0] === 'completed' ? 'true' : 'false',
      ro_status: estimate.ro_status[0] === 'completed' ? 'approved' : 'pending',
    };
    dispatch(sendEstimate(estimate.estimate_id[0], data, 'undoEstimateError'));
  };

  const onComplete = (estimate) => () => {
    const data = {
      vehicle_id: activeVehicle.id,
      ro_status: 'completed',
      completed_by_uid: currentUser.user,
      completed_date: getDateTime(),
      lock_estimate: 'true',
    };
    dispatch(sendEstimate(estimate.estimate_id[0], data, 'addEstimateError'));
  };

  const handleBulkActionConfirm = (task) => {
    setConfirmingTask(task);
  };

  const handleBulkActionButtonClick = (task, table) => {
    const bulkAction = task === 'Approve' ? onApprove : onDeny;
    if (!bulkAction) {
      console.error(`Invalid task: ${task}`);
      return;
    }
    try {
      const selectedRows = table.getSelectedRowModel().rows;
      selectedRows.forEach((row) => {
        bulkAction(row.original)();
      });
      table.resetRowSelection();
    } catch (error) {
      console.error('Error processing items:', error);
    } finally {
      setConfirmingTask(null);
    }
  };

  const onSend = (estimate) => () => {
    const data = {
      vehicle_id: activeVehicle.id,
      requested_by_uid: currentUser.user,
      requested_date: getDateTime(),
      ro_status: 'pending',
      lock_estimate: 'true',
    };
    dispatch(sendEstimate(estimate.estimate_id[0], data, 'addEstimateError'));
    const message = `Vehicle Vin:${activeVehicle.vin} Stock: ${activeVehicle.stock} requires your approval. Vehicle is currently located in the ${dispatch(
      lookupDepartmentById(activeVehicle.department_id)
    ).name} stage`;
  };

  // ====================== SELECT WORK ITEM - NOW FILTERED BY DEALERSHIP ======================
  const selectWorkItem = () => {
    const default_labor_rate = _.find(
      dealerSettings,
      (o) => o.key === 'default_labor_rate' && o.dealership === installation.subdomain
    );
    const default_labor_rate_value = default_labor_rate ? default_labor_rate.value : 100;
    const options = _.sortBy(visibleWorkItemPresets, 'name').map((option) => ({
      id: option.id,
      label: option.name,
      value: option.name,
      parts_price: option.parts_price,
      labor_units: option.labor_units,
      labor_price: Number(option.labor_price) > 0 ? option.labor_price : default_labor_rate_value,
      category: option.category,
      estimate_total: option.estimate_total,
      estimate: option.estimate_total,
      estimate_type: option.estimate_type,
      parts_price_editable: option.parts_price_editable,
      labor_price_editable: option.labor_price_editable,
      labor_units_editable: option.labor_units_editable,
      estimate_total_editable: option.estimate_total_editable,
    }));
    return options || [];
  };

  const onChangeWorkItem = (estimate_id) => (e) => {
    const errorType = 'Change WorkItem';
    const lock = e.value !== 'Blank Row';
    const default_labor_rate = _.find(
      dealerSettings,
      (o) => o.key === 'default_labor_rate' && o.dealership === installation.subdomain
    );
    const default_labor_rate_value = default_labor_rate ? default_labor_rate.value : 100;
    const labor_price = Number(e.labor_price) > 0 ? Number(e.labor_price) : default_labor_rate_value;
    const labor_total = Number(e.labor_units) * labor_price;
    const estimate_total = Number(e.parts_price) + labor_total;
    const data = {
      estimate_total: estimate_total,
      labor_price: labor_price,
      labor_units: e.labor_units,
      estimate_type: e.estimate_type,
      category: e.category,
      estimate: estimate_total,
      vehicle_id: activeVehicle.id,
      description: e.description,
      label_item_1: e.value,
      lock_estimate: lock,
      parts_price: e.parts_price,
      lock_item_1: e.parts_price_editable,
      lock_item_2: e.labor_units_editable,
      lock_item_3: e.labor_price_editable,
      lock_item_4: e.estimate_total_editable,
    };
    dispatch(sendEstimate(estimate_id, data, 'addEstimateError'));
  };

  const onChangeRecipients = (estimate_id) => (e) => {
    const errorType = 'Change WorkItem';
    const data = {
      recipients: e.id,
      vehicle_id: activeVehicle.id,
    };
    dispatch(sendEstimate(estimate_id, data, 'addEstimateError'));
  };

  useEffect(() => {
    if (appraisals) {
      const processedData = _.orderBy(appraisals, 'id', 'desc').filter(
        (estimate) => estimate.ro_status !== 'expense'
      );
      const total = processedData.reduce(
        (sum, estimate) => {
          const calculated = Number(estimate.parts_price) + (Number(estimate.labor_units) * Number(estimate.labor_price));
          const stored = Number(estimate.estimate_total) || 0;
          return sum + (stored > 0 ? stored : calculated);
        },
        0
      );
      setNewdata(
        processedData.map((estimate) => {
          return {
            estimate: [estimate.estimate_total],
            ro_number: [estimate.ro_number],
            parts_price: [estimate.parts_price],
            labor_price: [estimate.labor_price],
            labor_units: [estimate.labor_units],
            item_5_type: [estimate.item_5_type],
            description: [estimate.description],
            estimate_total: [estimate.estimate_total],
            ro_status: [estimate.ro_status],
            action: [estimate.ro_status],
            classification: [estimate.classification],
            category: [estimate.category],
            estimate_type: [estimate.estimate_type],
            estimate_id: [estimate.id],
            parts_note: [estimate.parts_note],
            parts_note_by_uid: [estimate.parts_note_by_uid],
            parts_note_by_date: [estimate.parts_note_by_date],
            requested_by_uid: [estimate.requested_by_uid],
            approved_by_uid: [estimate.approved_by_uid],
            approved_date: [estimate.approved_date],
            declined_by_uid: [estimate.declined_by_uid],
            declined_date: [estimate.declined_date],
            declined_reason: [estimate.declined_reason],
            lock_estimate: [estimate.lock_estimate],
            label_item_1: [estimate.label_item_1],
            recipients: [estimate.recipients],
            requested_date: [estimate.requested_date],
            counter_estimate: [estimate.counter_estimate],
            completed_by_uid: [estimate.completed_by_uid || ''],
            completed_date: [estimate.completed_date || ''],
            lock_item_1: estimate.lock_item_1,
            lock_item_2: estimate.lock_item_2,
            lock_item_3: estimate.lock_item_3,
            lock_item_4: estimate.lock_item_4,
          };
        })
      );
      setEstimatesTotal(total);
    } else {
      setNewdata([]);
      setEstimatesTotal(0);
    }
  }, [appraisals]);

  // Export Functions
  const exportableColumnKeys = [
    'category',
    'label_item_1',
    'description',
    'parts_price',
    'labor_units',
    'labor_price',
    'labor_total',
    'estimate_total',
    'ro_status',
  ];

  const handleExportRowsToCSV = (rows) => {
    if (!rows || rows.length === 0) return;
    const rowData = rows.map((row) =>
      exportableColumnKeys.reduce((acc, key) => {
        if (key === 'labor_total') {
          acc[key] = (Number(row.original.labor_units[0]) * Number(row.original.labor_price[0])).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = Number(row.original.parts_price[0]) + (Number(row.original.labor_units[0]) * Number(row.original.labor_price[0]));
          const stored = Number(row.original.estimate_total[0]) || 0;
          acc[key] = (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          acc[key] = row.original[key] ? row.original[key][0] : '';
        }
        return acc;
      }, {})
    );
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportDataToCSV = () => {
    if (!newdata || newdata.length === 0) return;
    const rowData = newdata.map((item) =>
      exportableColumnKeys.reduce((acc, key) => {
        if (key === 'labor_total') {
          acc[key] = (Number(item.labor_units[0]) * Number(item.labor_price[0])).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = Number(item.parts_price[0]) + (Number(item.labor_units[0]) * Number(item.labor_price[0]));
          const stored = Number(item.estimate_total[0]) || 0;
          acc[key] = (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          acc[key] = item[key] ? item[key][0] : '';
        }
        return acc;
      }, {})
    );
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportRowsToPDF = (rows) => {
    if (!rows || rows.length === 0) return;
    const doc = new jsPDF();
    const tableData = rows.map((row) =>
      exportableColumnKeys.map((key) => {
        if (key === 'labor_total') {
          return (Number(row.original.labor_units[0]) * Number(row.original.labor_price[0])).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = Number(row.original.parts_price[0]) + (Number(row.original.labor_units[0]) * Number(row.original.labor_price[0]));
          const stored = Number(row.original.estimate_total[0]) || 0;
          return (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          return row.original[key] ? row.original[key][0] : '';
        }
      })
    );
    const tableHeaders = exportableColumnKeys.map(
      (key) => columns.find((col) => col.accessorKey === key).header
    );
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });
    const newFilename = `${filename}_${activeVehicle.stock}`;
    doc.save(newFilename);
  };

  const handleExportDataToPDF = () => {
    if (!newdata || newdata.length === 0) return;
    const doc = new jsPDF();
    const vehicle = activeVehicle;
    const vehicleInfo = [
      [`VIN: ${vehicle.vin || 'N/A'}`],
      [`Stock: ${vehicle.stock || 'N/A'}`],
      [`Year: ${vehicle.year || 'N/A'}`],
      [`Make: ${vehicle.make || 'N/A'}`],
      [`Model: ${vehicle.model || 'N/A'}`],
    ];

    doc.text('Vehicle Information', 10, 10);
    autoTable(doc, {
      startY: 20,
      body: vehicleInfo,
    });

    const tableData = newdata.map((item) =>
      exportableColumnKeys.map((key) => {
        if (key === 'labor_total') {
          return (Number(item.labor_units[0]) * Number(item.labor_price[0])).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = Number(item.parts_price[0]) + (Number(item.labor_units[0]) * Number(item.labor_price[0]));
          const stored = Number(item.estimate_total[0]) || 0;
          return (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          return item[key] ? item[key][0] : '';
        }
      })
    );
    const tableHeaders = exportableColumnKeys.map(
      (key) => columns.find((col) => col.accessorKey === key).header
    );
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [tableHeaders],
      body: tableData,
    });

    const newFilename = `${filename}_${activeVehicle.stock}`;
    doc.save(newFilename);
  };

  const handlePrint = () => {
    if (!newdata || newdata.length === 0) return;
    const doc = new jsPDF();
    const vehicle = activeVehicle;
    const vehicleInfo = [
      [`VIN: ${vehicle.vin || 'N/A'}`],
      [`Stock: ${vehicle.stock || 'N/A'}`],
      [`Year: ${vehicle.year || 'N/A'}`],
      [`Make: ${vehicle.make || 'N/A'}`],
      [`Model: ${vehicle.model || 'N/A'}`],
    ];

    doc.text('Vehicle Information', 10, 10);
    autoTable(doc, {
      startY: 20,
      body: vehicleInfo,
    });

    const tableData = newdata.map((item) =>
      exportableColumnKeys.map((key) => {
        if (key === 'labor_total') {
          return (Number(item.labor_units[0]) * Number(item.labor_price[0])).toFixed(2);
        } else if (key === 'estimate_total') {
          const calculated = Number(item.parts_price[0]) + (Number(item.labor_units[0]) * Number(item.labor_price[0]));
          const stored = Number(item.estimate_total[0]) || 0;
          return (stored > 0 ? stored : calculated).toFixed(2);
        } else {
          return item[key] ? item[key][0] : '';
        }
      })
    );
    const tableHeaders = exportableColumnKeys.map(
      (key) => columns.find((col) => col.accessorKey === key).header
    );
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [tableHeaders],
      body: tableData,
    });

    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  };

  return (
    <ThemeProvider theme={tableTheme}>
      <div className="container-fluid pad-left-2 pad-right-2 table-create-new-work-item">
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            disabled={newdata.length === 0}
            onClick={handleExportDataToCSV}
            startIcon={<FileDownload />}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            disabled={newdata.length === 0}
            onClick={handleExportDataToPDF}
            startIcon={<FileDownload />}
            sx={{ ml: 1 }}
          >
            Export PDF
          </Button>
          <Button
            variant="contained"
            disabled={newdata.length === 0}
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{ ml: 1 }}
          >
            Print
          </Button>
        </Box>
        <Dialog open={flyoutOpen} onClose={() => setFlyoutOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Select Work Items</DialogTitle>
          <DialogContent sx={{ padding: 1 }}>
            <Box sx={{ mb: 2, display: 'flex', gap: '1rem' }}>
              <Button
                variant={selectedCategory === 'all' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedCategory('all');
                }}
              >
                All
              </Button>
              <Button
                variant={selectedCategory === 'mechanical' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedCategory('mechanical');
                }}
              >
                Mechanical
              </Button>
              <Button
                variant={selectedCategory === 'cosmetic' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedCategory('cosmetic');
                }}
              >
                Cosmetic
              </Button>
            </Box>
            <Grid
              container
              spacing={1}
              sx={{
                maxHeight: '60vh',
                overflowY: 'auto',
                justifyContent: 'space-between',
              }}
            >
              {presetData.map((item, index) => (
                <Grid key={index} size={3}>
                  <Box
                    sx={{
                      padding: '4px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: selectedPresets[index]
                        ? '#e3f2fd'
                        : appraisals.some((appraisal) => appraisal.label_item_1 === item.name)
                        ? '#e0e0e0'
                        : 'white',
                      textAlign: 'center',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedPresets((prev) => {
                        const newSelection = { ...prev };
                        newSelection[index] = !newSelection[index];
                        return newSelection;
                      });
                    }}
                  >
                    {item.name}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setFlyoutOpen(false);
            }} color="error">
              Cancel
            </Button>
            <Button 
              onClick={onClickMultiple} 
              color="success" 
              disabled={Object.keys(selectedPresets).length === 0}
            >
              Create Selected Work Items
            </Button>
          </DialogActions>
        </Dialog>
        <VehiclePartsSection vehicle={activeVehicle} tab={tab} />
        <MaterialReactTable
          tableRef={tableRef}
          columns={columns}
          data={newdata}
          enableRowSelection={(row) =>
            row.original.ro_status[0] === 'pending' && !isDisabled
          }
          pageSize={20}
          enableEditing
          editDisplayMode={'cell'}
          createDisplayMode={'custom'}
          initialState={{ 
            density: 'compact', 
            pagination: { pageIndex: 0, pageSize: 25 },
            columnVisibility: { labor_price: false }
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <Button variant="contained" onClick={onClickSingle}>
                  Create New Work Item
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setFlyoutOpen(true);
                  }}
                >
                  Create Multiple Work Items
                </Button>
                {confirmingTask ? (
                  <div className={'btn-group'} role="group">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleBulkActionButtonClick(confirmingTask, table)}
                    >
                      Confirm {confirmingTask} Selected
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setConfirmingTask(null)}
                    >
                      Back
                    </button>
                  </div>
                ) : (
                  <div className={'btn-group'} role="group">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleBulkActionConfirm('Approve')}
                      disabled={table.getSelectedRowModel().rows.length === 0 || isDisabled}
                    >
                      Approve Selected
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleBulkActionConfirm('Deny')}
                      disabled={table.getSelectedRowModel().rows.length === 0 || isDisabled}
                    >
                      Deny Selected
                    </button>
                  </div>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: '1rem' }}>
                <span>Parts Total: ${partsTotal.toFixed(2)}</span>
                <span>Labor Total: ${laborTotal.toFixed(2)}</span>
                <span>Total: ${estimates_total.toFixed(2)}</span>
                <span>Approved Total: ${approvedTotal.toFixed(2)}</span>
              </Box>
            </Box>
          )}
          renderDetailPanel={({ row }) => (
            <Box>
              {workItemPartsEnabled && (
                <AppraisalPartsSection
                  appraisalId={row.original.estimate_id[0]}
                  vehicleId={activeVehicle.id}
                  tab={tab}
                  appraisal={row.original}
                />
              )}
              <AppraisalAttachments
                appraisalId={row.original.estimate_id[0]}
                vehicleId={activeVehicle.id}
              />
            </Box>
          )}
        />
      </div>
    </ThemeProvider>
  );
};

export default NewEstimates;